import {ChangeDetectorRef, Component, ElementRef, HostBinding, inject, Input, OnDestroy, OnInit, Renderer2,} from '@angular/core';
import {Block, BlockType, BucketType, Group, Partial, PdfPart} from '@paperlessio/sdk/api/models';
import {DraggableService} from './draggable.service';
import {BlockService} from '@blocks/block/block.service';
import {Subscription} from 'rxjs';
import {AbsoluteDragService} from '@shared/absolute-drag/absolute-drag.service';
import {ABSOLUTE_DRAG_PARENT} from '@shared/absolute-drag/injection-tokens';
import {Platform} from '@angular/cdk/platform';

@Component({
  selector: 'app-draggable',
  templateUrl: './draggable.component.html',
  styleUrls: ['./draggable.component.scss']
})
export class DraggableComponent implements OnInit, OnDestroy {
  @HostBinding('class.dragging') isDragging = false;
  @HostBinding('class.hidden') isHidden = false;
  @HostBinding('class.overlay')
  get isOverlay(): boolean {
    return this.block?.parent instanceof PdfPart;
  }

  @HostBinding('attr.draggable')
  get isDraggable(): boolean | null {
    return !this.disabled && (!this.hasHandle || this.draggable);
  }

  set isDraggable(value) {
    this.draggable = value;
  }

  @Input() block?: Block;
  @Input() partial?: Partial;
  @Input() disabled = false;
  @Input() clone = false;

  public hasHandle = false;

  private draggable: boolean | null = null;
  private eventListeners: any[] = [];
  private subs = new Subscription();

  private elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private draggableService = inject(DraggableService, {optional: true});
  private absoluteDragService = inject(AbsoluteDragService, {optional: true});
  private absoluteDragDirective = inject(ABSOLUTE_DRAG_PARENT, {optional: true});
  private platform = inject(Platform, {optional: true});
  private cdr = inject(ChangeDetectorRef);
  private renderer = inject(Renderer2);
  private blockService = inject(BlockService);

  ngOnInit(): void {
    if (this.draggableService) {
      this.eventListeners.push(
        this.renderer.listen(
          this.elementRef.nativeElement,
          'dragstart',
          event => this.dragstart(event)
        ));

      this.eventListeners.push(
        this.renderer.listen(
          this.elementRef.nativeElement,
          'dragend',
          () => this.dragend()
        ));
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();

    // When the user drags a block to a different page, we navigate to that page.
    // And if we do that, the original draggable fires it's onDestroy.
    // That would kill our dragging process, so we don't unsubscribe here to save it.
    // We know that this is a memory leak problem.
    // TODO: Fix the underlying problem that the drag & drop is handled by the dragged
    // component because it could be destroyed at any time (page change)
    if (!this.isDragging) {
      this.eventListeners.forEach(listener => {
        if (listener && typeof listener === 'function') {
          listener();
        }
      });
    }
  }

  public dragstart(event: DragEvent) {
    // save the block in draggableService. when in overlay mode, we dont get it from dragend() below
    this.draggableService.block = this.block;

    if (this.isOverlay) {
      // We can attach data to the drag directive here if we ever need to
      // this.absoluteDragDirective.data = this.block;

      this.absoluteDragService.startDrag(this.elementRef, this.absoluteDragDirective, event);

      if (this.platform.FIREFOX) {
        // Firefox doesn't like the canvas approach from below and this just works flawlessly
        const divElement = document.createElement('div');
        event.dataTransfer?.setDragImage(divElement, 0, 0);
      } else {
        // Chrome works perfectly with this solution
        const blankCanvas: any = document.createElement('canvas');
        event.dataTransfer?.setDragImage(blankCanvas, 0, 0);
        document.body?.appendChild(blankCanvas);

        setTimeout(() => {
          document.body.removeChild(blankCanvas);
        }, 1000);
      }

      event.dataTransfer.effectAllowed = 'move';
    } else {
      this.elementRef.nativeElement.classList.add('drag-clone');
      event.dataTransfer.setDragImage(this.elementRef.nativeElement, 10, 10);
    }

    // Set block as data for drag & drop between different tabs
    if (!!event.dataTransfer?.items) {
      const data = JSON.stringify(this.block);
      event.dataTransfer.items.add(data, 'application/vnd.paperless+json');
    }

    this.isDragging = true;

    requestAnimationFrame(() => {
      this.elementRef.nativeElement.classList.remove('drag-clone');
      this.isHidden = !this.clone;
      this.draggableService.isDragging$.next(true);
      this.cdr.markForCheck();
    });

    event.stopPropagation();
  }

  public dragend() {
    if (this.block) {
      this.draggableService.block = this.block;
      this.apply();
    } else {
      this.subs.add(
        this.blockService
          .allFromBucket(BucketType.Partial, this.partial.id)
          .subscribe(blocks => {
            this.draggableService.duplicate = true;
            this.draggableService.block = new Group({id: this.partial.root_block_id, type: BlockType.Group});
            this.draggableService.partial = this.partial;
            this.draggableService.partialChildren = blocks;
            this.apply();
          }));
    }
  }

  private apply() {
    this.isDragging = false;
    this.isHidden = false;
    this.isDraggable = null;

    if (this.isOverlay) {
      this.absoluteDragService.reset();
    }

    this.cdr.markForCheck();
    this.draggableService.apply();
  }
}
